import { DestroyRef, effect, inject, Injectable, signal } from '@angular/core';
import { ProductTourService } from 'src/app/ui/services/product-tour/product-tour.service';

interface TrackerUiSettings {
  newDesign: boolean;
  layout: 'left' | 'right';
  showHotKeyTips: boolean;
  expandQueryBuilderOnHover: boolean;
  showActiveChips: boolean;
  filtersUnderDimensions: boolean;
  presetsListCollapsed: boolean;
  savedStatesCollapsed: boolean;
  preferredTimezone: boolean;
}

const STORAGE_KEY = 'tracker-ui-settings';
const MOBILE_BREAKPOINT = 768;

const hasStoredTrackerSettings = localStorage.getItem(STORAGE_KEY) !== null;

const DEFAULT_SETTINGS: TrackerUiSettings = {
  newDesign: true,
  layout: 'right',
  showHotKeyTips: false,
  expandQueryBuilderOnHover: false,
  showActiveChips: false,
  filtersUnderDimensions: false,
  presetsListCollapsed: true,
  savedStatesCollapsed: true,
  preferredTimezone: false,
};

function loadSettings(): TrackerUiSettings {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return { ...DEFAULT_SETTINGS, ...JSON.parse(stored) };
    }
  } catch (e) {
    console.error('Failed to load tracker UI settings from localStorage', e);
  }
  return DEFAULT_SETTINGS;
}

@Injectable({
  providedIn: 'root'
})
export class TrackerUiService {
  private readonly initialSettings = loadSettings();
  private readonly destroyRef = inject(DestroyRef);
  private readonly productTourService = inject(ProductTourService);

  public readonly isFirstVisit = !hasStoredTrackerSettings;

  public newDesign = signal<boolean>(this.initialSettings.newDesign);
  public layout = signal<'left' | 'right'>(this.initialSettings.layout);
  public showHotKeyTips = signal<boolean>(this.initialSettings.showHotKeyTips);
  public expandQueryBuilderOnHover = signal<boolean>(this.initialSettings.expandQueryBuilderOnHover);
  public showActiveChips = signal<boolean>(this.initialSettings.showActiveChips);
  public filtersUnderDimensions = signal<boolean>(this.initialSettings.filtersUnderDimensions);
  public presetsListCollapsed = signal<boolean>(this.initialSettings.presetsListCollapsed);
  public savedStatesCollapsed = signal<boolean>(this.initialSettings.savedStatesCollapsed);
  public preferredTimezone = signal<boolean>(this.initialSettings.preferredTimezone);

  public queryBuilderCollapsed = signal<boolean>(false);
  public settingsOpen = signal<boolean>(false);

  // Mobile support
  public isMobile = signal<boolean>(window.innerWidth < MOBILE_BREAKPOINT);
  public mobileQueryBuilderOpen = signal<boolean>(false);

  // Signal to trigger adding a filter with pre-selected dimension
  public pendingFilterDimension = signal<string | null>(null);

  public startAddFilterForDimension(dimension: string): void {
    this.pendingFilterDimension.set(dimension);
  }

  // Signal to trigger opening sort edit for a dimension
  public pendingOrderDimension = signal<string | null>(null);

  public startEditOrderForDimension(dimension: string): void {
    this.pendingOrderDimension.set(dimension);
  }

  constructor() {
    this.initMobileDetection();

    this.productTourService.registerAction('tracker.enable-new-design', () => {
      this.newDesign.set(true);
    });
    this.destroyRef.onDestroy(() =>
      this.productTourService.unregisterAction('tracker.enable-new-design')
    );

    effect(() => {
      this.saveSettings({
        newDesign: this.newDesign(),
        layout: this.layout(),
        showHotKeyTips: this.showHotKeyTips(),
        expandQueryBuilderOnHover: this.expandQueryBuilderOnHover(),
        showActiveChips: this.showActiveChips(),
        filtersUnderDimensions: this.filtersUnderDimensions(),
        presetsListCollapsed: this.presetsListCollapsed(),
        savedStatesCollapsed: this.savedStatesCollapsed(),
        preferredTimezone: this.preferredTimezone(),
      });
    });
  }

  private initMobileDetection(): void {
    const mediaQuery = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`);

    const handleChange = (e: MediaQueryListEvent | MediaQueryList) => {
      this.isMobile.set(e.matches);
      // Close mobile panel when switching to desktop
      if (!e.matches) {
        this.mobileQueryBuilderOpen.set(false);
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    this.destroyRef.onDestroy(() => mediaQuery.removeEventListener('change', handleChange));

    // Initial check
    handleChange(mediaQuery);

    // Auto-open query builder on mobile on initial load
    if (mediaQuery.matches) {
      this.mobileQueryBuilderOpen.set(true);
    }
  }

  public openMobileQueryBuilder(): void {
    this.mobileQueryBuilderOpen.set(true);
  }

  public closeMobileQueryBuilder(): void {
    this.mobileQueryBuilderOpen.set(false);
  }

  public toggleMobileQueryBuilder(): void {
    this.mobileQueryBuilderOpen.update(v => !v);
  }

  private saveSettings(settings: TrackerUiSettings): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch (e) {
      console.error('Failed to save tracker UI settings to localStorage', e);
    }
  }

  public expandQueryBuilder(): void {
    this.queryBuilderCollapsed.set(false);
  }

  public collapseQueryBuilder(): void {
    this.queryBuilderCollapsed.set(true);
    this.settingsOpen.set(false);
  }

  public toggleQueryBuilder(): void {
    const willCollapse = !this.queryBuilderCollapsed();
    this.queryBuilderCollapsed.update(v => !v);
    if (willCollapse) {
      this.settingsOpen.set(false);
    }
  }

  public openSettings(): void {
    this.settingsOpen.set(true);
  }

  public closeSettings(): void {
    this.settingsOpen.set(false);
  }

  public toggleSettings(): void {
    this.settingsOpen.update(v => !v);
  }
}
